<template>
  <div class="c-virtual-table w-full h-full flex flex-col">
    <div class="w-full flex c-virtual-table__header">
      <div v-for="(column, idx) in columns" :key="column.key" class="font-bold px-2 py-4" :class="column.class">
        <slot :column="column" :index="idx" :name="`column-${column.key}`">
          <span :class="{ 'ml-3': !idx }">
            {{ column.title }}
          </span>
        </slot>
      </div>
    </div>
    <div class="overflow-hidden c-virtual-table__content">
      <div v-if="tableDataWithIndexes?.length === 0" class="flex py-8 justify-center h-full">
        <span>{{ t('canvasUI.common.noData') }}</span>
      </div>
      <div
        v-else
        ref="virtualizerContainerRef"
        class="overflow-y-auto contain-strict"
        :style="{ height: scrollerHeight }"
      >
        <div
          v-if="virtualRows?.length"
          :style="{
            height: `${totalVirtualizerSize}px`,
            width: '100%',
            position: 'relative',
          }"
        >
          <div
            v-for="{ key, index, start } in virtualRows"
            :key="`${key}`"
            :ref="measureElement"
            :data-index="index"
            :style="{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              transform: `translateY(${start}px)`,
            }"
          >
            <CVirtualTableRow
              class="cursor-pointer"
              :columns="columns"
              :item="tableDataWithIndexes[index]"
              :selected-ids="props.selectedIds"
              @click="handleRowClick"
              @dragging="handleDragging"
              @move="handleMove"
            >
              <template v-for="column in columns" :key="column.key" v-slot:[column.key]="{ item }">
                <slot :index="item.index" :item="item" :name="column.key">{{ item[column.key] }}</slot>
              </template>
            </CVirtualTableRow>
          </div>
        </div>
      </div>
    </div>
    <div class="c-virtual-table__footer" />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, nextTick, onMounted, onUnmounted, reactive } from 'vue'
import { useI18n } from 'vue-i18n'

import CVirtualTableRow from './CVirtualTableRow.vue'
import { useVirtualizer } from '@tanstack/vue-virtual'
import useThemeVars from '@lib/composables/useThemeVars'

const { t } = useI18n()

interface TableDataItem {
  id: number | string
  [key: string]: any
}

interface TableColumn {
  key: string
  title: string
  class?: string
}

const props = defineProps<{
  tableData: TableDataItem[]
  selectedIds: (number | string)[]
  columns: TableColumn[]
  estimatedRowHeight: number
}>()

const themeVars = useThemeVars()

const emit = defineEmits(['row:click', 'move'])

const tableDataWithIndexes = computed(() => props.tableData?.map((item, index) => ({ ...item, index })))

function handleRowClick(item: TableDataItem, event: MouseEvent) {
  emit('row:click', item, event)
}

function handleMove({ targetItemId, itemIds }: { targetItemId: number; itemIds: number[] }) {
  emit('move', { targetItemId, itemIds })
}

const contentContainer = ref<any | null>(null)

function handleDragging(isDragging: boolean) {
  if (isDragging) {
    contentContainer.value?.$el?.addEventListener?.('mousemove', handleMouseMove)
  } else {
    contentContainer.value?.$el?.removeEventListener?.('mousemove', handleMouseMove)
  }
}

function handleMouseMove() {
  // just to trigger scroll behavior
}

const windowDimensions = reactive({
  height: window.innerHeight,
  width: window.innerWidth,
})

const virtualizerContainerRef = ref<HTMLElement | null>(null)

const rowVirtualizer = useVirtualizer(
  computed(() => ({
    count: props.tableData.length,
    getScrollElement: () => virtualizerContainerRef.value,
    estimateSize: () => props.estimatedRowHeight,
  })),
)

const virtualRows = computed(() => rowVirtualizer.value.getVirtualItems())
const totalVirtualizerSize = computed(() => rowVirtualizer.value.getTotalSize())
const measureElement = (el?: Element) => {
  if (!el) return

  nextTick().then(() => {
    rowVirtualizer.value.measureElement(el)
  })

  return undefined
}

const scrollerHeight = computed(() => {
  if (virtualizerContainerRef.value) {
    const offsetTop = virtualizerContainerRef.value.getBoundingClientRect().top
    const availableHeight = windowDimensions.height - offsetTop
    return `${availableHeight}px`
  }
  return ''
})

const onResize = () => {
  windowDimensions.height = window.innerHeight
  windowDimensions.width = window.innerWidth
}

onMounted(() => {
  window.addEventListener('resize', onResize)
})

onUnmounted(() => {
  window.removeEventListener('resize', onResize)
})
</script>

<style lang="scss">
.c-virtual-table__footer,
.c-virtual-table__content {
  border-top: 1px solid v-bind('themeVars.primary5');
}
</style>
